// RewardComponents — Free Stock Rewards sheet + brand constellation
// Matches the design in the attached photos: white sheet on light gray,
// with a constellation of 3 brand icons (Apple, NVIDIA, Amazon) connected
// by faint lines and surrounded by dots.

import { useEffect, useState } from 'react';
import { STOCKS, REWARD_OFFER } from '../../data/stocks';

export function BrandConstellation({ size = 220, brandIds }: { size?: number; brandIds?: string[] }) {
  // Position three brands in a triangle and draw connecting lines + dots
  const ids = brandIds ?? REWARD_OFFER.brandIds;
  const brands = ids
    .map((id) => STOCKS.find((s) => s.id.endsWith(id) || s.symbol.toLowerCase() === id))
    .filter(Boolean) as typeof STOCKS;

  const w = size, h = size;
  const cx = w / 2, cy = h / 2;
  const r = size * 0.32;
  const positions = brands.map((_, i) => {
    const angle = (-Math.PI / 2) + (i * 2 * Math.PI / brands.length);
    return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
  });

  // Generate dot grid
  const dots: { x: number; y: number; r: number }[] = [];
  for (let i = 0; i < 80; i++) {
    const a = (i / 80) * Math.PI * 2;
    const dist = size * 0.18 + (i % 7) * (size * 0.04);
    const x = cx + dist * Math.cos(a * 2.3) - 8;
    const y = cy + dist * Math.sin(a * 1.7) - 8;
    if (x > 4 && x < w - 4 && y > 4 && y < h - 4) dots.push({ x, y, r: (i % 3 === 0) ? 1.8 : 1.2 });
  }

  return (
    <div className="relative mx-auto" style={{ width: w, height: h }} aria-hidden>
      <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 h-full w-full">
        {positions.length >= 2 && positions.slice(0, -1).map((p, i) => {
          const next = positions[i + 1] || positions[0];
          return <line key={i} x1={p.x} y1={p.y} x2={next.x} y2={next.y} stroke="#E5E5EA" strokeWidth="1" strokeDasharray="2 4" />;
        })}
        {dots.map((d, i) => (
          <circle key={i} cx={d.x} cy={d.y} r={d.r} fill="#E5E5EA" />
        ))}
      </svg>
      {brands.map((b, i) => (
        <div
          key={b.id}
          className="absolute flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-4 border-white bg-white shadow-[0_4px_20px_rgba(0,0,0,0.08)]"
          style={{ left: positions[i].x, top: positions[i].y }}
        >
          <img src={b.icon} alt={b.name} className="h-10 w-10 rounded-full" />
        </div>
      ))}
    </div>
  );
}

// Countdown chip — used in dashboard and reward sheet
export function CountdownChip({ seconds, label = 'Expires in' }: { seconds: number; label?: string }) {
  const [s, setS] = useState(seconds);
  useEffect(() => {
    const t = setInterval(() => setS((x) => Math.max(0, x - 1)), 1000);
    return () => clearInterval(t);
  }, []);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  return (
    <div className="rounded-full bg-tf-surface-1 px-3 py-1 text-[13px] tabular-nums font-semibold text-tf-text-1">
      {label === 'Expires in' ? '1d ' : ''}
      {String(hours).padStart(2, '0')}h {String(mins).padStart(2, '0')}m {String(secs).padStart(2, '0')}s
    </div>
  );
}

// Free Stock Rewards sheet (bottom sheet modal)
export function FreeStockRewardSheet({
  open, onClose, onClaim,
}: { open: boolean; onClose: () => void; onClaim: () => void }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="free-reward-sheet"
    >
      <div
        className="w-full max-w-[420px] rounded-t-[24px] bg-white p-6 pb-8 shadow-[0_-12px_40px_rgba(0,0,0,0.15)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-[#E5E5EA]" />
        <div className="mb-4 flex justify-center">
          <BrandConstellation size={220} />
        </div>
        <h2 className="text-center text-[22px] font-bold tracking-tight text-tf-text-1">{REWARD_OFFER.title}</h2>
        <p className="mt-1 text-center text-[14px] text-tf-text-3">{REWARD_OFFER.subtitle}</p>
        <div className="mt-3 flex justify-center">
          <CountdownChip seconds={172774} />
        </div>
        <button
          onClick={onClaim}
          data-testid="reward-claim-btn"
          className="mt-5 flex h-[52px] w-full items-center justify-center rounded-full bg-tf-text-1 text-[16px] font-semibold text-white active:scale-[0.99]"
        >
          Redeem now
        </button>
      </div>
    </div>
  );
}

// RewardUnlocked page — "Apple Inc. — Free stock unlocked!" (matches photo)
export function RewardUnlockedSheet({ stock, onClose }: { stock: any; onClose: () => void }) {
  if (!stock) return null;
  return (
    <div className="fixed inset-0 z-50 bg-white">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-white/85 px-4 backdrop-blur-xl">
        <button onClick={onClose} className="text-[17px] text-tf-blue">Close</button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Reward</div>
        <div className="w-10" />
      </div>
      <div className="px-6 pt-12">
        <div className="flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-[0_4px_20px_rgba(0,0,0,0.08)]">
            <img src={stock.icon} alt={stock.name} className="h-12 w-12 rounded-full" />
          </div>
        </div>
        <h1 className="mt-6 text-center text-[28px] font-bold tracking-tight text-tf-text-1">{stock.name} Inc.</h1>
        <p className="mt-1 text-center text-[15px] text-tf-text-3">Free stock unlocked!</p>

        <div className="mt-8 overflow-hidden rounded-[16px] bg-white border border-tf-border">
          <DetailRow label="Stock" value={`${stock.name} Inc. (${stock.symbol})`} />
          <DetailRow label="Unlock date" value={new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} />
          <DetailRow label="Reward value" value={`$${REWARD_OFFER.stockValue.toFixed(2)}`} />
          <DetailRow label="Shares received" value={`≈ ${(REWARD_OFFER.stockValue / stock.price).toFixed(3)} ${stock.symbol}`} />
          <DetailRow label="Price at reward" value={`$${stock.price.toFixed(2)}`} />
          <DetailRow label="Reward type" value="Promotional reward" last />
        </div>

        <div className="mt-4 overflow-hidden rounded-[16px] bg-white border border-tf-border">
          <button onClick={() => alert('Invite friends flow')} className="flex w-full items-center gap-3 px-4 py-3 active:bg-[#E5E5EA]/60">
            <svg viewBox="0 0 24 24" className="h-5 w-5 text-tf-text-1" fill="none" stroke="currentColor" strokeWidth="1.8">
              <circle cx="9" cy="8" r="3" /><circle cx="17" cy="9" r="2.5" /><path d="M3 20c0-3 3-5 6-5s6 2 6 5M14 14c2 0 5 1 5 4" strokeLinecap="round" />
            </svg>
            <div className="flex-1 text-left">
              <div className="text-[15px] font-semibold text-tf-text-1">Invite friends, earn more</div>
              <div className="text-[13px] text-tf-text-3">Get free stock rewards when your friends start investing</div>
            </div>
            <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <div className={['flex items-center justify-between px-4 py-3', last ? '' : 'border-b border-tf-border'].join(' ')}>
      <span className="text-[15px] text-tf-text-3">{label}</span>
      <span className="text-[15px] font-medium text-tf-text-1">{value}</span>
    </div>
  );
}
